import React from 'react'
import { Option } from '../../types'

export const handleKeyDown = <T>(
  e: React.KeyboardEvent,
  optionsConfig: {
    isOpen: boolean
    setIsOpen: (value: boolean) => void
    setHighlightedIndex: (value: number | ((prev: number) => number)) => void
    highlightedIndex: number
    inputValue?: string
    availableOptions: Option<T>[]
    ignoredOptions?: T[]
    hasNewOption?: boolean
    handleAddNewOption?: (input: string) => void
    handleOptionClick: (option: Option<T>) => void
    closeOnSelect?: boolean
    onBlur?: () => void
  }
) => {
  const {
    isOpen,
    setIsOpen,
    setHighlightedIndex,
    highlightedIndex,
    inputValue = '',
    availableOptions,
    ignoredOptions,
    hasNewOption = false,
    handleAddNewOption,
    handleOptionClick,
    onBlur,
    closeOnSelect = true
  } = optionsConfig

  if (!isOpen) {
    // Enter on a closed dropdown is left to the surrounding container (e.g. applying the filters)
    if (e.key === 'Enter') e.preventDefault()
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      setIsOpen(true)
      setHighlightedIndex(0)
    }
    return
  }

  const filteredOptions = availableOptions.filter(
    (option) => !ignoredOptions?.includes(option.value)
  )
  const optionsLength = (hasNewOption ? 1 : 0) + filteredOptions.length

  if (optionsLength === 0) return

  switch (e.key) {
    case 'Tab':
      setIsOpen(false)
      onBlur?.()
      setHighlightedIndex(-1)
      break
    case 'ArrowDown':
      e.preventDefault()
      setHighlightedIndex((prev) => (prev + 1) % optionsLength)
      break
    case 'ArrowUp':
      e.preventDefault()
      setHighlightedIndex((prev) => (prev - 1 + optionsLength) % optionsLength)
      break
    case 'Enter':
      e.preventDefault()
      if (highlightedIndex === -1) return

      // The Enter picks an option, so it must not also reach the surrounding container
      e.nativeEvent.stopPropagation()
      if (hasNewOption && highlightedIndex === 0) {
        handleAddNewOption?.(inputValue)
      } else {
        const selectedIndex = hasNewOption ? highlightedIndex - 1 : highlightedIndex
        handleOptionClick(filteredOptions[selectedIndex])
        if (closeOnSelect) setIsOpen(false)
        // A multi select stays open, so the next Enter is left to the surrounding container
        setHighlightedIndex(-1)
      }
      break
    case 'Escape':
      setIsOpen(false)
      setHighlightedIndex(-1)
      break
  }
}
